/* Standalone carousel: no dependency on theme.js, .button or .is-selected. */
class MajaSlider extends HTMLElement {
  connectedCallback() {
    if (this.controller) return;
    this.slides = Array.from(this.querySelectorAll('[data-maja-slider-slide]'));
    if (!this.slides.length) return;
    this.controller = new AbortController();
    const options = { signal: this.controller.signal };
    this.index = Math.max(0, this.slides.findIndex(slide => slide.dataset.majaSliderSelected === 'true'));
    this.autoplay = this.dataset.majaSliderAutoplay === 'true' && this.slides.length > 1;
    this.delay = Math.max(1, Number(this.dataset.majaSliderDelay) || 5) * 1000;
    this.waitForVideo = this.dataset.majaSliderWaitVideo === 'true';
    this.mobile = matchMedia('(max-width: 699px)');
    this.reducedMotion = matchMedia('(prefers-reduced-motion: reduce)');
    this.visible = false;
    this.paused = false;
    this.hovered = false;
    this.focused = false;
    this.editorSelected = false;
    this.animations = [];

    this.addEventListener('click', event => this.onClick(event), options);
    this.addEventListener('keydown', event => this.onKeydown(event), options);
    this.addEventListener('pointerdown', event => this.onPointerDown(event), options);
    this.addEventListener('pointerup', event => this.onPointerUp(event), options);
    this.addEventListener('pointercancel', () => { this.pointer = null; }, options);
    // En iOS (Safari/Chrome) un swipe horizontal sobre el slider también dispara el gesto
    // de atrás/adelante del navegador y muestra sus flechas de sistema. Se cancela el
    // touchmove solo cuando el gesto es horizontal, para no bloquear el scroll vertical.
    this.addEventListener('touchstart', event => {
      const touch = event.touches[0];
      this.touch = event.touches.length === 1 ? { x: touch.clientX, y: touch.clientY, horizontal: null } : null;
    }, { ...options, passive: true });
    this.addEventListener('touchmove', event => {
      if (!this.touch || event.touches.length !== 1) return;
      const touch = event.touches[0];
      const dx = Math.abs(touch.clientX - this.touch.x);
      const dy = Math.abs(touch.clientY - this.touch.y);
      if (this.touch.horizontal === null && (dx > 8 || dy > 8)) this.touch.horizontal = dx > dy;
      if (this.touch.horizontal && event.cancelable) event.preventDefault();
    }, { ...options, passive: false });
    this.addEventListener('touchend', () => { this.touch = null; }, { ...options, passive: true });
    this.addEventListener('touchcancel', () => { this.touch = null; }, { ...options, passive: true });
    this.addEventListener('pointerenter', event => {
      if (event.pointerType === 'mouse') { this.hovered = true; this.schedule(); }
    }, options);
    this.addEventListener('pointerleave', () => { this.hovered = false; this.schedule(); }, options);
    this.addEventListener('focusin', () => { this.focused = true; this.schedule(); }, options);
    this.addEventListener('focusout', event => {
      if (!this.contains(event.relatedTarget)) { this.focused = false; this.schedule(); }
    }, options);
    this.addEventListener('ended', event => {
      if (event.target === this.activeVideo && this.waitForVideo && this.canRotate) this.next(false);
    }, { ...options, capture: true });
    this.addEventListener('playing', event => {
      if (event.target === this.activeVideo) {
        this.videoFailed = false;
        this.videoPlaying = true;
        this.schedule();
      }
    }, { ...options, capture: true });
    this.addEventListener('error', event => {
      if (event.target === this.activeVideo) { this.videoFailed = true; this.schedule(); }
    }, { ...options, capture: true });
    this.addEventListener('volumechange', () => this.updateSound(), { ...options, capture: true });
    document.addEventListener('visibilitychange', () => {
      this.syncMedia();
      this.schedule();
    }, options);
    this.mobile.addEventListener('change', () => { this.syncMedia(); this.schedule(); }, options);
    this.addEventListener('shopify:block:select', event => {
      const index = this.slides.findIndex(slide => slide === event.target || slide.contains(event.target) || slide.dataset.majaSliderBlockId === event.detail?.blockId);
      if (index < 0) return;
      this.editorSelected = true;
      this.select(index, { instant: true });
    }, options);
    this.addEventListener('shopify:block:deselect', () => { this.editorSelected = false; this.schedule(); }, options);

    this.observer = new IntersectionObserver(entries => {
      this.visible = entries[0].isIntersecting;
      if (this.visible && !this.revealed) {
        this.revealed = true;
        if (this.dataset.majaSliderInitialTransition === 'true') this.animateSlide();
      }
      this.syncMedia();
      this.schedule();
    }, { threshold: 0.1 });
    this.observer.observe(this);
    this.updateState();
    this.preloadAdjacent();
  }

  disconnectedCallback() {
    clearTimeout(this.timer);
    this.controller?.abort();
    this.controller = null;
    this.observer?.disconnect();
    this.animations?.forEach(animation => animation.cancel());
    this.querySelectorAll('video').forEach(video => video.pause());
    this.activeVideo = null;
    this.pointer = null;
  }

  get canRotate() {
    return this.autoplay && this.visible && !document.hidden && !this.paused && !this.hovered && !this.focused && !this.editorSelected;
  }

  select(index, { instant = false, announce = false } = {}) {
    if (!this.slides.length || !Number.isInteger(index)) return;
    index = (index % this.slides.length + this.slides.length) % this.slides.length;
    if (index === this.index) { this.schedule(); return; }
    const previous = this.slides[this.index];
    if (previous.contains(document.activeElement)) this.focus({ preventScroll: true });
    this.index = index;
    this.updateState();
    this.preloadAdjacent();
    this.syncMedia(true);
    if (!instant) this.animateSlide();
    else this.animations.forEach(animation => animation.cancel());
    this.schedule();
    if (announce) this.querySelector('.maja-slider__status').textContent = this.slides[index].getAttribute('aria-label');
    this.dispatchEvent(new CustomEvent('maja-slider:change', { bubbles: true, detail: { index, blockId: this.slides[index].dataset.majaSliderBlockId } }));
  }

  next(announce = true) { this.select(this.index + 1, { announce }); }
  previous() { this.select(this.index - 1, { announce: true }); }

  updateState() {
    this.slides.forEach((slide, index) => {
      const selected = index === this.index;
      slide.dataset.majaSliderSelected = String(selected);
      slide.setAttribute('aria-hidden', String(!selected));
      slide.inert = !selected;
    });
    this.querySelectorAll('[data-maja-slider-go]').forEach(button => {
      button.setAttribute('aria-current', String(Number(button.dataset.majaSliderGo) === this.index));
    });
  }

  schedule() {
    clearTimeout(this.timer);
    if (!this.canRotate) return;
    // If playback is blocked or media fails, fall back to the regular cycle.
    if (this.waitForVideo && this.activeVideo && this.videoPlaying && !this.videoFailed && !this.activeVideo.ended) return;
    this.timer = setTimeout(() => { if (this.canRotate) this.next(false); }, this.delay);
  }

  syncMedia(reset = false) {
    const videos = Array.from(this.slides[this.index].querySelectorAll('video'));
    const current = videos.find(video => getComputedStyle(video).display !== 'none');
    if (current !== this.activeVideo || reset) {
      this.videoFailed = false;
      this.videoPlaying = false;
      if (current) {
        current.muted = true;
        current.currentTime = 0;
      }
    }
    this.activeVideo = current;
    this.querySelectorAll('video').forEach(video => {
      video.loop = !this.autoplay;
      if (video !== current || !this.visible || document.hidden) {
        video.pause();
        if (video !== current) video.muted = true;
      }
    });
    if (current && this.visible && !document.hidden && current.paused) {
      const attempt = current.play();
      attempt?.catch(() => {
        if (this.activeVideo !== current || !this.isConnected) return;
        this.videoFailed = true;
        this.schedule();
      });
    }
    this.updateSound();
  }

  updateSound() {
    const button = this.slides?.[this.index]?.querySelector('[data-maja-slider-action="sound"]');
    if (!button) return;
    const audible = this.activeVideo && !this.activeVideo.muted;
    button.setAttribute('aria-pressed', String(Boolean(audible)));
    button.setAttribute('aria-label', audible ? button.dataset.majaSliderMuteLabel : button.dataset.majaSliderUnmuteLabel);
  }

  preloadAdjacent() {
    for (const index of [this.index, (this.index + 1) % this.slides.length, (this.index - 1 + this.slides.length) % this.slides.length]) {
      this.slides[index].querySelectorAll('img[loading="lazy"]').forEach(image => { image.loading = 'eager'; });
      this.slides[index].querySelectorAll('video[preload="none"]').forEach(video => { video.preload = 'metadata'; });
    }
  }

  animateSlide() {
    this.animations.forEach(animation => animation.cancel());
    this.animations = [];
    if (this.reducedMotion.matches) return;
    const slide = this.slides[this.index];
    this.animations.push(slide.animate([{ opacity: 0 }, { opacity: 1 }], { duration: 800, easing: 'ease-out' }));
    const media = slide.querySelector('.maja-slider__media');
    this.animations.push(media.animate([{ transform: 'scale(1.12)' }, { transform: 'scale(1)' }], { duration: 800, easing: 'ease-out' }));
    const content = slide.querySelector('.maja-slider__content');
    this.animations.push(content.animate([{ opacity: 0, transform: 'translateY(24px)' }, { opacity: 1, transform: 'translateY(0)' }], { duration: 600, easing: 'ease-out' }));
  }

  onClick(event) {
    if (performance.now() < (this.suppressClickUntil || 0) && event.target.closest('.maja-slider__slide-link')) {
      event.preventDefault();
      return;
    }
    const dot = event.target.closest('[data-maja-slider-go]');
    if (dot) { this.select(Number(dot.dataset.majaSliderGo), { announce: true }); return; }
    const button = event.target.closest('[data-maja-slider-action]');
    if (!button) return;
    switch (button.dataset.majaSliderAction) {
      case 'previous': this.previous(); break;
      case 'next': this.next(); break;
      case 'sound':
        if (this.activeVideo) { this.activeVideo.muted = !this.activeVideo.muted; this.updateSound(); }
        break;
      case 'pause':
        this.paused = !this.paused;
        button.setAttribute('aria-pressed', String(this.paused));
        button.setAttribute('aria-label', this.paused ? 'Reanudar presentación' : 'Pausar presentación');
        this.schedule();
        break;
      case 'next-section':
        this.closest('.maja-slider-section')?.nextElementSibling?.scrollIntoView({ block: 'start', behavior: this.reducedMotion.matches ? 'instant' : 'smooth' });
        break;
    }
  }

  onKeydown(event) {
    if (event.altKey || event.ctrlKey || event.metaKey || event.target.matches('input, textarea, select, [contenteditable="true"]')) return;
    const rtl = getComputedStyle(this).direction === 'rtl';
    if (event.key === 'ArrowRight') { event.preventDefault(); rtl ? this.previous() : this.next(); }
    if (event.key === 'ArrowLeft') { event.preventDefault(); rtl ? this.next() : this.previous(); }
    if (event.key === 'Home') { event.preventDefault(); this.select(0, { announce: true }); }
    if (event.key === 'End') { event.preventDefault(); this.select(this.slides.length - 1, { announce: true }); }
  }

  onPointerDown(event) {
    if (!event.isPrimary || event.button !== 0 || event.target.closest('button, a:not(.maja-slider__slide-link)')) return;
    this.pointer = { id: event.pointerId, x: event.clientX, y: event.clientY };
    // Keep taps on a linked slide native; cancel the click only after a swipe.
    if (!event.target.closest('a')) this.setPointerCapture(event.pointerId);
  }

  onPointerUp(event) {
    const start = this.pointer;
    this.pointer = null;
    if (!start || start.id !== event.pointerId) return;
    const dx = event.clientX - start.x;
    const dy = event.clientY - start.y;
    if (Math.abs(dx) < 50 || Math.abs(dx) < Math.abs(dy) * 1.2) return;
    this.suppressClickUntil = performance.now() + 400;
    const rtl = getComputedStyle(this).direction === 'rtl';
    (dx < 0) !== rtl ? this.next() : this.previous();
  }
}

if (!customElements.get('maja-slider')) customElements.define('maja-slider', MajaSlider);
